import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Instagram, Music } from 'lucide-react';
import AppleLogo from './AppleLogo';
import { toast } from 'sonner';
import { artistInfo } from '../mock';
import { useApi } from '../contexts/ApiContext';

const BookingSection = () => {
  const { API_BASE_URL } = useApi();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    event_type: '',
    event_date: '',
    set_length: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState(null); // { type: 'success' | 'error', message: string }
  const [fieldErrors, setFieldErrors] = useState({}); // { fieldName: 'error message' }
  const [touchedFields, setTouchedFields] = useState({}); // Track which fields have been touched

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Basic sanitization on input
    const sanitizedValue = value.replace(/<[^>]*>/g, ''); // Remove HTML tags
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleBlur = (fieldName) => {
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate field on blur
    validateField(fieldName, formData[fieldName]);
  };

  const validateField = (fieldName, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    const errors = { ...fieldErrors };

    switch (fieldName) {
      case 'name':
        if (!trimmedValue || trimmedValue.length < 1) {
          errors.name = 'Name is required';
        } else if (trimmedValue.length > 200) {
          errors.name = 'Name must be 200 characters or less';
        } else {
          delete errors.name;
        }
        break;
      case 'email':
        if (!trimmedValue) {
          errors.email = 'Email is required';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(trimmedValue)) {
            errors.email = 'Please enter a valid email address';
          } else if (trimmedValue.length > 254) {
            errors.email = 'Email address is too long';
          } else {
            delete errors.email;
          }
        }
        break;
      case 'message':
        if (!trimmedValue || trimmedValue.length < 1) {
          errors.message = 'Message is required';
        } else if (trimmedValue.length > 5000) {
          errors.message = 'Message must be 5000 characters or less';
        } else {
          delete errors.message;
        }
        break;
      default:
        break;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAllFields = () => {
    const errors = {};
    
    // Validate required fields
    const trimmedName = formData.name.trim();
    if (!trimmedName || trimmedName.length < 1) {
      errors.name = 'Name is required';
    } else if (trimmedName.length > 200) {
      errors.name = 'Name must be 200 characters or less';
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        errors.email = 'Please enter a valid email address';
      } else if (trimmedEmail.length > 254) {
        errors.email = 'Email address is too long';
      }
    }

    const trimmedMessage = formData.message.trim();
    if (!trimmedMessage || trimmedMessage.length < 1) {
      errors.message = 'Message is required';
    } else if (trimmedMessage.length > 5000) {
      errors.message = 'Message must be 5000 characters or less';
    }

    // Mark all required fields as touched
    setTouchedFields({
      name: true,
      email: true,
      message: true
    });
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitFeedback(null);

    // Validate all fields before submission
    const validationErrors = {};
    const trimmedName = formData.name.trim();
    if (!trimmedName || trimmedName.length < 1) {
      validationErrors.name = 'Name is required';
    } else if (trimmedName.length > 200) {
      validationErrors.name = 'Name must be 200 characters or less';
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      validationErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        validationErrors.email = 'Please enter a valid email address';
      } else if (trimmedEmail.length > 254) {
        validationErrors.email = 'Email address is too long';
      }
    }

    const trimmedMessage = formData.message.trim();
    if (!trimmedMessage || trimmedMessage.length < 1) {
      validationErrors.message = 'Message is required';
    } else if (trimmedMessage.length > 5000) {
      validationErrors.message = 'Message must be 5000 characters or less';
    }

    if (Object.keys(validationErrors).length > 0) {
      // Mark all required fields as touched
      setTouchedFields({
        name: true,
        email: true,
        message: true
      });
      setFieldErrors(validationErrors);
      
      // Scroll to first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      setTimeout(() => {
        const errorElement = document.getElementById(firstErrorField);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }, 100);
      
      toast.error('Please fix the errors in the form before submitting', {
        duration: 5000,
      });
      setIsSubmitting(false);
      return;
    }

    // Values already validated and trimmed above

    // Enhanced sanitization (remove HTML tags, scripts, and dangerous patterns)
    const sanitize = (str) => {
      if (!str) return '';
      return str
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+\s*=/gi, '') // Remove event handlers
        .trim();
    };

    const sanitizedData = {
      name: sanitize(trimmedName).substring(0, 200),
      email: sanitize(trimmedEmail).substring(0, 254),
      message: sanitize(trimmedMessage).substring(0, 5000),
      event_type: sanitize(formData.event_type)?.substring(0, 50) || null,
      event_date: sanitize(formData.event_date)?.substring(0, 50) || null,
      set_length: sanitize(formData.set_length)?.substring(0, 50) || null
    };

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 30000); // 30 second timeout

    try {
      const response = await fetch(`${API_BASE_URL}/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
        signal: abortController.signal,
        credentials: 'omit', // Don't send cookies
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        // Validate response structure
        if (result && typeof result === 'object' && result.success !== false) {
          const successMsg = result.message || 'Your booking inquiry has been sent successfully! JLEE will review your request and respond directly by email.';
          toast.success(successMsg, {
            duration: 5000,
          });
          setSubmitFeedback({ type: 'success', message: successMsg });
          // Clear form and errors
          setFormData({ name: '', email: '', message: '', event_type: '', event_date: '', set_length: '' });
          setFieldErrors({});
          setTouchedFields({});
        } else {
          // Server returned OK but with error in response
          const errorMsg = result?.message || result?.detail || 'Failed to send message. Please try again.';
          toast.error(errorMsg, {
            duration: 6000,
          });
          setSubmitFeedback({ type: 'error', message: errorMsg });
          setIsSubmitting(false);
          return;
        }
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to submit. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // If JSON parsing fails, use default message
        }
        
        
        // For 400 errors (validation), show error and don't fallback
        if (response.status === 400) {
          toast.error(errorMessage, {
            duration: 6000,
          });
          setSubmitFeedback({ type: 'error', message: errorMessage });
          setIsSubmitting(false);
          return;
        }
        
        // For 500 errors (server errors), show error with option to use email
        if (response.status === 500) {
          const serverErrorMsg = errorMessage || 'Server error. Your message was not sent. Please try again or use the email link on the right to contact directly.';
          toast.error(serverErrorMsg, {
            duration: 8000,
          });
          setSubmitFeedback({ type: 'error', message: serverErrorMsg });
          setIsSubmitting(false);
          return;
        }
        
        // For other HTTP errors, show error
        const httpErrorMsg = errorMessage || `Unable to send message (Error ${response.status}). Please try again or use the email link on the right.`;
        toast.error(httpErrorMsg, {
          duration: 6000,
        });
        setSubmitFeedback({ type: 'error', message: httpErrorMsg });
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      clearTimeout(timeoutId);

      // Don't handle AbortError (user cancelled, component unmounted, or timeout)
      if (error.name === 'AbortError') {
        if (abortController.signal.aborted) {
          toast.error('Request timed out. Please try again or use the email link on the right.');
        }
        setIsSubmitting(false);
        return;
      }
      
      // Check if it's a network error
      const isNetworkError = error instanceof TypeError || 
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('NetworkError') ||
                            error.message?.includes('CORS') ||
                            error.message?.includes('network');
      
      if (isNetworkError) {
        const errorMsg = `Cannot connect to server. Please check your internet connection and try again, or use the email link on the right to contact directly.`;
        toast.error(errorMsg, {
          duration: 8000,
        });
        setSubmitFeedback({ type: 'error', message: errorMsg });
      } else {
        // Other errors - sanitize error message for security
        const safeErrorMessage = error.message?.replace(/<[^>]*>/g, '').substring(0, 200) || 'An unexpected error occurred. Your message was not sent. Please try again.';
        toast.error(safeErrorMessage, {
          duration: 6000,
        });
        setSubmitFeedback({ type: 'error', message: safeErrorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-black relative" id="booking">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="mb-12 sm:mb-16 text-center">
          <p id="booking-section-tab" className="section-tab">Booking</p>
          <h2 id="booking-section-title" className="section-title">Get In Touch</h2>
          <p className="body-medium text-white/85 max-w-2xl mx-auto mb-3 sm:mb-4 px-4 sm:px-0">
            Book JLEE for your next event. Professional DJ sets available for clubs, festivals, private events, and venues.
            Include venue details, date, and technical requirements.
          </p>
          <p className="body-small text-white/70 max-w-2xl mx-auto px-4 sm:px-0">
            Versatile sets available — from high-energy dubstep to chill ambient vibes, tailored to your event.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 lg:gap-16">
          {/* Left - Contact Form */}
          <div className="box-card p-4 sm:p-6 md:p-8 overflow-hidden">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6 w-full max-w-full" noValidate>
              {/* Name */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-name" htmlFor="name" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Name *</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('name')}
                  required
                  autoComplete="name"
                  className={`bg-black border-white/25 text-white h-12 sm:h-14 text-base w-full booking-input-base ${
                    fieldErrors.name ? 'border-red-500 focus-visible:border-red-500' : ''
                  }`}
                  placeholder="Your name"
                  aria-required="true"
                  aria-invalid={fieldErrors.name ? 'true' : 'false'}
                  aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                />
                {fieldErrors.name && touchedFields.name && (
                  <p id="name-error" className="body-small text-red-400 mt-1" role="alert">
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-email" htmlFor="email" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Email *</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('email')}
                  required
                  autoComplete="email"
                  inputMode="email"
                  className={`bg-black border-white/25 text-white h-12 sm:h-14 text-base placeholder:text-white/70 w-full booking-input-base ${
                    fieldErrors.email ? 'border-red-500 focus-visible:border-red-500' : ''
                  }`}
                  placeholder="your@email.com"
                  aria-required="true"
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
                {fieldErrors.email && touchedFields.email && (
                  <p id="email-error" className="body-small text-red-400 mt-1" role="alert">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Event Type */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-event-type" htmlFor="event_type" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Event Type</span>
                </Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                >
                  <SelectTrigger 
                    id="event_type"
                    name="event_type"
                    aria-labelledby="booking-label-event-type"
                    className="bg-black border-white/25 text-white h-12 sm:h-14 rounded-none text-base data-[placeholder]:text-white/70 w-full booking-input-base"
                  >
                    <SelectValue id="booking-select-event-type-value" placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/30 text-white rounded-none shadow-lg z-50">
                    <SelectItem value="Club / Bar" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Club / Bar</SelectItem>
                    <SelectItem value="Show" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Show</SelectItem>
                    <SelectItem value="Private Event" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Private Event</SelectItem>
                    <SelectItem value="Festival" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Festival</SelectItem>
                    <SelectItem value="Other" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Event Date */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-event-date" htmlFor="event_date" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Event Date</span>
                </Label>
                <div className="relative w-full max-w-full">
                  <Input
                    id="event_date"
                    name="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    className="bg-black border-white/25 text-white h-12 sm:h-14 text-base booking-date-input w-full booking-input-base"
                  />
                </div>
              </div>

              {/* Set Length */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-set-length" htmlFor="set_length" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Set Length</span>
                </Label>
                <Select
                  value={formData.set_length}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, set_length: value }))}
                >
                  <SelectTrigger 
                    id="set_length"
                    name="set_length"
                    aria-labelledby="booking-label-set-length"
                    className="bg-black border-white/25 text-white h-12 sm:h-14 rounded-none text-base data-[placeholder]:text-white/70 w-full booking-input-base"
                  >
                    <SelectValue id="booking-select-set-length-value" placeholder="Select set length" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/30 text-white rounded-none shadow-lg z-50">
                    <SelectItem value="30 min" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">30 min</SelectItem>
                    <SelectItem value="60 min" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">60 min</SelectItem>
                    <SelectItem value="90 min" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">90 min</SelectItem>
                    <SelectItem value="Not sure" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2 w-full max-w-full overflow-hidden">
                <Label id="booking-label-message" htmlFor="message" className="text-white body-medium booking-label booking-label-visible">
                  <span className="booking-label-text">Message *</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('message')}
                  required
                  rows={6}
                  className={`bg-black border-white/25 text-white resize-none text-base min-h-[120px] sm:min-h-[140px] placeholder:text-white/70 w-full booking-input-base ${
                    fieldErrors.message ? 'border-red-500 focus-visible:border-red-500' : ''
                  }`}
                  placeholder="Your message..."
                  aria-required="true"
                  aria-invalid={fieldErrors.message ? 'true' : 'false'}
                  aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                />
                {fieldErrors.message && touchedFields.message && (
                  <p id="message-error" className="body-small text-red-400 mt-1" role="alert">
                    {fieldErrors.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-2">
                <button 
                  type="submit" 
                  className="btn-primary w-full sm:w-auto sm:min-w-[160px] booking-button-base"
                  disabled={isSubmitting}
                  aria-label={isSubmitting ? 'Submitting form' : 'Submit booking form'}
                >
                  <span className="text-black">{isSubmitting ? 'Sending...' : 'Submit'}</span>
                </button>
              </div>

              {/* Inline confirmation/error (in addition to toast) */}
              {submitFeedback?.message ? (
                <div
                  className={`mt-4 box-card p-4 text-center border ${
                    submitFeedback.type === 'error' 
                      ? 'border-red-500/50 bg-red-500/10' 
                      : 'border-brand-turquoise/50 bg-brand-turquoise/10'
                  }`}
                  role={submitFeedback.type === 'error' ? 'alert' : 'status'}
                  aria-live={submitFeedback.type === 'error' ? 'assertive' : 'polite'}
                >
                  <p className={`body-medium font-medium ${
                    submitFeedback.type === 'error' 
                      ? 'text-red-400' 
                      : 'text-brand-turquoise'
                  }`}>
                    {submitFeedback.type === 'success' && '✓ '}
                    {submitFeedback.type === 'error' && '✗ '}
                    {submitFeedback.message}
                  </p>
                </div>
              ) : null}
            </form>
          </div>

          {/* Right - Contact Info & Links */}
          <div className="space-y-6 sm:space-y-8">
            {/* Social Links */}
            <div className="box-card p-4 sm:p-6 md:p-8">
              <h3 id="booking-links-title" className="subsection-title mb-4 sm:mb-6">Links</h3>
              <div className="space-y-3 sm:space-y-4">
                <a
                  href="https://music.apple.com/ca/artist/jlee/1756341411"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 box-card group transition-all hover:border-[var(--brand-turquoise)]/40 booking-button-base"
                  aria-label="Visit JLEE on Apple Music"
                >
                  <AppleLogo size={20} color="var(--brand-turquoise)" className="group-hover:scale-110 transition-transform flex-shrink-0" />
                  <div>
                    <p className="body-medium text-white">Apple Music</p>
                  </div>
                </a>
                <a
                  href="https://on.soundcloud.com/p7lR72fb7GYKSPuCUZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 box-card group transition-all hover:border-[var(--brand-turquoise)]/40 booking-button-base"
                  aria-label="Visit JLEE on SoundCloud"
                >
                  <Music className="text-brand-turquoise group-hover:scale-110 transition-transform flex-shrink-0" size={20} />
                  <div>
                    <p className="body-medium text-white">SoundCloud</p>
                  </div>
                </a>
                <a
                  href={`https://instagram.com/${artistInfo.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 box-card group transition-all hover:border-[var(--brand-turquoise)]/40 booking-button-base"
                  aria-label={`Visit JLEE on Instagram: ${artistInfo.instagram}`}
                >
                  <Instagram className="text-brand-turquoise group-hover:scale-110 transition-transform flex-shrink-0" size={20} />
                  <div>
                    <p className="body-medium text-white">Instagram</p>
                    <p className="body-small text-white/70">{artistInfo.instagram}</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Email Contact */}
            <div className="box-card p-4 sm:p-6 md:p-8">
              <h3 id="booking-email-title" className="subsection-title mb-3 sm:mb-4">Email</h3>
              <div className="flex items-center gap-3 sm:gap-4">
                <Mail className="text-brand-turquoise flex-shrink-0" size={20} />
                <div>
                  <a 
                    href={`mailto:${artistInfo.email}`}
                    className="body-medium text-white hover:text-brand-turquoise transition-colors break-all"
                  >
                    {artistInfo.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-[#121212] border border-white/10 p-3 sm:p-4 md:p-5 text-center">
              <p className="body-medium text-white/60">Kelowna, BC</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
